/**
 * Client-side Excel processing using SheetJS (xlsx library)
 * This handles all Excel parsing and processing without server involvement
 * Optimized for large datasets with chunked processing
 */

// Note: You'll need to install xlsx library: npm install xlsx
import * as XLSX from 'xlsx';
import {
  detectFacilityColumn,
  validateRequiredMappings
} from './excelParser';
import { log, warn, error } from '@/utils/debugLogger';
import { API_LIMITS, UI_THRESHOLDS } from '@/constants/apiLimits';

/**
 * Convert Excel serial date number to JavaScript Date
 * Excel stores dates as days since January 1, 1900
 */
function excelDateToJSDate(excelDate) {
  // Excel's epoch starts on January 1, 1900
  const excelEpoch = new Date(1900, 0, 1);
  // Excel incorrectly treats 1900 as a leap year, so we need to subtract 1 day for dates after Feb 28, 1900
  const jsDate = new Date(excelEpoch.getTime() + (excelDate - 1) * 24 * 60 * 60 * 1000);

  // Adjust for Excel's leap year bug (if date is after February 28, 1900)
  if (excelDate > 59) {
    jsDate.setTime(jsDate.getTime() - 24 * 60 * 60 * 1000);
  }

  return jsDate;
}

/**
 * Get cell format information from Excel worksheet
 */
function getCellDisplayValues(worksheet) {
  const cellDisplayValues = {};
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];

      if (cell) {
        // If cell has formatted display value (w) and it's different from raw value (v)
        if (cell.w && cell.v !== undefined && String(cell.w) !== String(cell.v)) {
          if (!cellDisplayValues[R]) cellDisplayValues[R] = {};
          cellDisplayValues[R][C] = cell.w;

          // Debug: Log when we find formatted values
          if (typeof cell.v === 'number' && cell.v > 40000 && cell.v < 50000) {
            log('clientExcelProcessor', `Cell ${cellAddress}: raw=${cell.v}, formatted="${cell.w}"`);
          }
        }
      }
    }
  }

  log('clientExcelProcessor', 'getCellDisplayValues result count:', Object.keys(cellDisplayValues).length);
  return cellDisplayValues;
}

/**
 * Convert Excel value using display value if available
 */
function convertExcelValueWithDisplay(value, rowIndex, colIndex, cellDisplayValues) {
  // Check if this cell has a formatted display value
  if (cellDisplayValues[rowIndex] && cellDisplayValues[rowIndex][colIndex]) {
    const displayValue = cellDisplayValues[rowIndex][colIndex];

    // If the raw value is a number that looks like an Excel date serial number,
    // use the Excel date conversion instead of the display value
    if (typeof value === 'number' && value > 1 && value < 100000) {
      // This is likely an Excel date serial number
      const actualDate = excelDateToJSDate(value);
      const formattedDate = formatDateString(actualDate);
      return formattedDate;
    }

    return displayValue;
  }

  // Return original value if no display formatting
  return value;
}

/**
 * Format JavaScript Date to readable string
 * @param {Date} date - JavaScript Date object
 * @returns {string} Formatted date string
 */
function formatDateString(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    return '';
  }

  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Parse Excel file on the client-side with progress tracking
 * @param {File} file - Excel file from file input
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Parsed Excel data with analysis
 */
export async function parseExcelFile(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        const progress = Math.round((e.loaded / e.total) * 50); // First 50% for reading
        onProgress({ stage: 'reading', progress, message: 'Reading Excel file...' });
      }
    };
    
    reader.onload = async (e) => {
      try {
        if (onProgress) {
          onProgress({ stage: 'parsing', progress: 50, message: 'Parsing Excel data...' });
        }

        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: '', // Default value for empty cells
          blankrows: false // Skip blank rows
        });
        
        // Get cell display values (formatted values like dates)
        const cellDisplayValues = getCellDisplayValues(worksheet);

        if (jsonData.length === 0) {
          throw new Error('Excel file appears to be empty');
        }
        
        // Extract headers (first row)
        const headers = jsonData[0] || [];
        const rawDataRows = jsonData.slice(1);

        // Convert Excel values using display values (handles dates and other formatting)
        const dataRows = rawDataRows.map((row, rowIndex) =>
          row.map((cell, colIndex) => convertExcelValueWithDisplay(cell, rowIndex + 1, colIndex, cellDisplayValues))
        );
        
        if (onProgress) {
          onProgress({ 
            stage: 'analyzing', 
            progress: 75, 
            message: `Analyzing ${dataRows.length} rows...` 
          });
        }

        // Validate Excel row limit
        if (dataRows.length > API_LIMITS.MAX_EXCEL_ROWS) {
          reject({
            success: false,
            error: `Excel file contains ${dataRows.length} rows. Maximum allowed is ${API_LIMITS.MAX_EXCEL_ROWS}.`
          });
          return;
        }

        // For large datasets, use optimized analysis
        const analysis = dataRows.length > API_LIMITS.MAX_EXCEL_ROWS
          ? await analyzeLargeExcelData(headers, dataRows, onProgress)
          : analyzeExcelData(headers, dataRows);

        if (onProgress) {
          onProgress({ stage: 'complete', progress: 100, message: 'Analysis complete!' });
        }

        resolve({
          success: true,
          data: {
            fileName: file.name,
            sheets: workbook.SheetNames,
            headers,
            dataRows,
            totalRows: dataRows.length,
            isLargeDataset: dataRows.length > API_LIMITS.MAX_EXCEL_ROWS,
            ...analysis
          }
        });
        
      } catch (error) {
        reject({
          success: false,
          error: error.message || 'Failed to parse Excel file'
        });
      }
    };
    
    reader.onerror = () => {
      reject({
        success: false,
        error: 'Failed to read file'
      });
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Analyze Excel data to provide intelligent suggestions
 * @param {Array} headers - Column headers
 * @param {Array} dataRows - Data rows
 * @returns {Object} Analysis results
 */
function analyzeExcelData(headers, dataRows) {
  // Create sample data for preview
  const sampleData = {};
  const sampleRowCount = Math.min(5, dataRows.length);
  
  headers.forEach((header, index) => {
    if (header && header.trim()) {
      sampleData[header] = [];
      for (let i = 0; i < sampleRowCount; i++) {
        if (dataRows[i] && dataRows[i][index] !== undefined) {
          const value = dataRows[i][index];
          if (value !== null && value !== '') {
            sampleData[header].push(String(value).trim());
          }
        }
      }
    }
  });
  
  // Detect facility column and unique facilities
  const facilityColumn = detectFacilityColumn(headers);
  let facilitiesDetected = [];
  
  if (facilityColumn) {
    const facilityColumnIndex = headers.indexOf(facilityColumn);
    const facilitySet = new Set();
    
    dataRows.forEach(row => {
      if (row[facilityColumnIndex] && String(row[facilityColumnIndex]).trim()) {
        facilitySet.add(String(row[facilityColumnIndex]).trim());
      }
    });
    
    facilitiesDetected = Array.from(facilitySet).sort();
  }
  
  // Data quality analysis
  const dataQuality = analyzeDataQuality(headers, dataRows);
  
  return {
    detectedColumns: headers.filter(h => h && h.trim()),
    sampleData,
    facilityColumn,
    facilitiesDetected,
    dataQuality,
    columnStats: getColumnStatistics(headers, dataRows)
  };
}

/**
 * Optimized analysis for large datasets
 * @param {Array} headers - Column headers
 * @param {Array} dataRows - Data rows
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Analysis results
 */
async function analyzeLargeExcelData(headers, dataRows) {
  const sampleSize = Math.min(UI_THRESHOLDS.EXCEL_SAMPLE_SIZE, dataRows.length);
  const sampleData = {};
  const facilityColumn = detectFacilityColumn(headers);
  const facilitySet = new Set();

  // Sample first 5 rows for preview
  headers.forEach((header, index) => {
    if (header && header.trim()) {
      sampleData[header] = [];
      for (let i = 0; i < Math.min(5, dataRows.length); i++) {
        if (dataRows[i] && dataRows[i][index] !== undefined) {
          const value = dataRows[i][index];
          if (value !== null && value !== '') {
            sampleData[header].push(String(value).trim());
          }
        }
      }
    }
  });

  // Sample facility detection for large datasets
  if (facilityColumn) {
    const facilityColumnIndex = headers.indexOf(facilityColumn);
    const step = Math.max(1, Math.floor(dataRows.length / sampleSize));

    for (let i = 0; i < dataRows.length; i += step) {
      const row = dataRows[i];
      if (row[facilityColumnIndex] && String(row[facilityColumnIndex]).trim()) {
        facilitySet.add(String(row[facilityColumnIndex]).trim());
      }

      // Limit facility detection to API limit
      if (facilitySet.size > API_LIMITS.MAX_FACILITY_ASSIGNMENTS) break;
    }
  }
  
  const facilitiesDetected = Array.from(facilitySet).sort();
  
  // Lightweight data quality analysis for large datasets
  const dataQuality = {
    totalRows: dataRows.length,
    isLargeDataset: true,
    samplingRate: sampleSize / dataRows.length,
    columns: {}
  };
  
  headers.forEach(header => {
    if (header && header.trim()) {
      dataQuality.columns[header] = {
        hasData: sampleData[header] && sampleData[header].length > 0,
        sampleValues: sampleData[header] ? sampleData[header].length : 0
      };
    }
  });
  
  return {
    detectedColumns: headers.filter(h => h && h.trim()),
    sampleData,
    facilityColumn,
    facilitiesDetected,
    dataQuality,
    columnStats: {},
  };
}

/**
 * Analyze data quality and provide insights
 * @param {Array} headers - Column headers
 * @param {Array} dataRows - Data rows
 * @returns {Object} Data quality metrics
 */
function analyzeDataQuality(headers, dataRows) {
  const quality = {
    totalRows: dataRows.length,
    emptyRows: 0,
    columns: {}
  };
  
  // Initialize column stats
  headers.forEach(header => {
    if (header && header.trim()) {
      quality.columns[header] = {
        totalValues: 0,
        emptyValues: 0,
        uniqueValues: new Set(),
        dataTypes: new Set()
      };
    }
  });
  
  // Analyze each row
  dataRows.forEach(row => {
    let isEmpty = true;
    
    headers.forEach((header, index) => {
      if (header && header.trim() && quality.columns[header]) {
        const value = row[index];
        
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          isEmpty = false;
          quality.columns[header].totalValues++;
          quality.columns[header].uniqueValues.add(String(value));
          quality.columns[header].dataTypes.add(detectDataType(value));
        } else {
          quality.columns[header].emptyValues++;
        }
      }
    });
    
    if (isEmpty) {
      quality.emptyRows++;
    }
  });
  
  // Convert Sets to counts for serialization
  Object.keys(quality.columns).forEach(header => {
    const col = quality.columns[header];
    col.uniqueValueCount = col.uniqueValues.size;
    col.dataTypeCount = col.dataTypes.size;
    col.completeness = (col.totalValues / (col.totalValues + col.emptyValues)) * 100;
    
    // Don't serialize the Sets
    delete col.uniqueValues;
    delete col.dataTypes;
  });
  
  return quality;
}

/**
 * Get column statistics for analysis
 * @param {Array} headers - Column headers
 * @param {Array} dataRows - Data rows
 * @returns {Object} Column statistics
 */
function getColumnStatistics(headers, dataRows) {
  const stats = {};
  
  headers.forEach((header, index) => {
    if (header && header.trim()) {
      const values = dataRows
        .map(row => row[index])
        .filter(val => val !== null && val !== undefined && String(val).trim() !== '');
      
      stats[header] = {
        count: values.length,
        isEmpty: values.length === 0,
        avgLength: values.length > 0 ? 
          values.reduce((sum, val) => sum + String(val).length, 0) / values.length : 0,
        hasNumeric: values.some(val => !isNaN(val) && !isNaN(parseFloat(val))),
        hasText: values.some(val => isNaN(val) || isNaN(parseFloat(val)))
      };
    }
  });
  
  return stats;
}

/**
 * Detect data type of a value
 * @param {*} value - Value to analyze
 * @returns {string} Detected data type
 */
function detectDataType(value) {
  if (value === null || value === undefined || value === '') {
    return 'empty';
  }
  
  const strValue = String(value).trim();
  
  // Check for numbers
  if (!isNaN(strValue) && !isNaN(parseFloat(strValue))) {
    return strValue.includes('.') ? 'decimal' : 'integer';
  }
  
  // Check for dates
  if (!isNaN(Date.parse(strValue))) {
    return 'date';
  }
  
  // Check for boolean-like values
  if (['true', 'false', 'yes', 'no', '1', '0'].includes(strValue.toLowerCase())) {
    return 'boolean';
  }
  
  return 'text';
}

/**
 * Process mapped data and prepare for server submission
 * Uses chunked processing for large datasets to prevent memory issues
 * @param {Array} headers - Original Excel headers
 * @param {Array} dataRows - Original Excel data rows
 * @param {Object} columnMappings - User-defined column mappings
 * @param {Object} options - Processing options
 * @returns {Object} Processed data ready for server
 */
export function processExcelData(headers, dataRows, columnMappings, options = {}) {
  const {
    tagType = 'equipment'
  } = options;
  
  // Validate mappings
  const validation = validateRequiredMappings(columnMappings, tagType);
  if (!validation.isValid) {
    throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
  }
  
  // For large datasets, use chunked processing
  if (dataRows.length > API_LIMITS.MAX_EXCEL_ROWS) {
    throw new Error(`Excel file contains ${dataRows.length} rows. Maximum allowed is ${API_LIMITS.MAX_EXCEL_ROWS}.`);
  }

  // Standard processing for smaller datasets
  return processStandardDataset(headers, dataRows, columnMappings, options, validation);
}

/**
 * Process standard-sized datasets (≤10k rows)
 */
function processStandardDataset(headers, dataRows, columnMappings, options, validation) {
  const {
    facilityColumn = null,
    filterByFacility = null
  } = options;
  
  const processedData = [];
  
  dataRows.forEach((row, rowIndex) => {
    // Skip empty rows
    if (!row || row.every(cell => !cell || String(cell).trim() === '')) {
      return;
    }
    
    // Check facility filter
    if (facilityColumn && filterByFacility) {
      const facilityIndex = headers.indexOf(facilityColumn);
      const rowFacility = row[facilityIndex];
      
      if (!rowFacility || String(rowFacility).trim() !== filterByFacility) {
        return; // Skip this row
      }
    }
    
    // Create mapped record
    const record = {
      _originalRowIndex: rowIndex + 2, // +2 because we skip header row and arrays are 0-indexed
      _originalData: {}
    };
    
    // Map each column according to user mappings
    Object.entries(columnMappings).forEach(([excelColumn, avaField]) => {
      const columnIndex = headers.indexOf(excelColumn);
      const value = row[columnIndex];
      
      // Store original data for reference
      record._originalData[excelColumn] = value;
      
      // Map to AVA field
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        record[avaField] = String(value).trim();
      }
    });
    
    // Only include records that have valid Tag No. (name field)
    if (record.name && String(record.name).trim() !== '') {
      processedData.push(record);
    }
  });
  
  return {
    success: true,
    data: {
      records: processedData,
      summary: {
        totalRowsProcessed: dataRows.length,
        recordsCreated: processedData.length,
        skippedRows: dataRows.length - processedData.length,
        facilityFiltered: !!filterByFacility,
        mappingsApplied: Object.keys(columnMappings).length
      },
      validation,
      metadata: {
        tagType: options.tagType,
        facilityColumn,
        filterByFacility,
        processedAt: new Date().toISOString()
      }
    }
  };
}

/**
 * Process large datasets using chunked approach
 */
async function processLargeDataset(headers, dataRows, columnMappings, options) {
  const {
    facilityColumn = null,
    filterByFacility = null,
    tagType = 'equipment',
    onProgress = null
  } = options;
  
  const chunkSize = 1000;
  const totalChunks = Math.ceil(dataRows.length / chunkSize);
  const processedData = [];
  let processedRows = 0;
  
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const startIndex = chunkIndex * chunkSize;
    const endIndex = Math.min(startIndex + chunkSize, dataRows.length);
    const chunk = dataRows.slice(startIndex, endIndex);
    
    // Process chunk
    chunk.forEach((row, rowIndexInChunk) => {
      const actualRowIndex = startIndex + rowIndexInChunk;
      
      // Skip empty rows
      if (!row || row.every(cell => !cell || String(cell).trim() === '')) {
        return;
      }
      
      // Check facility filter
      if (facilityColumn && filterByFacility) {
        const facilityIndex = headers.indexOf(facilityColumn);
        const rowFacility = row[facilityIndex];
        
        if (!rowFacility || String(rowFacility).trim() !== filterByFacility) {
          return; // Skip this row
        }
      }
      
      // Create mapped record
      const record = {
        _originalRowIndex: actualRowIndex + 2,
        _originalData: {}
      };
      
      // Map each column according to user mappings
      Object.entries(columnMappings).forEach(([excelColumn, avaField]) => {
        const columnIndex = headers.indexOf(excelColumn);
        const value = row[columnIndex];
        
        // Store original data for reference
        record._originalData[excelColumn] = value;
        
        // Map to AVA field
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          record[avaField] = String(value).trim();
        }
      });
      
      // Only include records that have valid Tag No. (name field)
      if (record.name && String(record.name).trim() !== '') {
        processedData.push(record);
      }
    });
    
    processedRows += chunk.length;
    
    // Report progress
    if (onProgress) {
      onProgress({
        stage: 'processing',
        completed: chunkIndex + 1,
        total: totalChunks,
        progress: Math.round(((chunkIndex + 1) / totalChunks) * 100),
        processedRows,
        recordsCreated: processedData.length,
        message: `Processing chunk ${chunkIndex + 1}/${totalChunks}...`
      });
    }
    
    // Yield control to prevent UI freezing
    await new Promise(resolve => setTimeout(resolve, 0));
  }
  
  const validation = validateRequiredMappings(columnMappings, tagType);
  
  return {
    success: true,
    data: {
      records: processedData,
      summary: {
        totalRowsProcessed: dataRows.length,
        recordsCreated: processedData.length,
        skippedRows: dataRows.length - processedData.length,
        facilityFiltered: !!filterByFacility,
        mappingsApplied: Object.keys(columnMappings).length,
        processedInChunks: true,
        chunksProcessed: totalChunks
      },
      validation,
      metadata: {
        tagType,
        facilityColumn,
        filterByFacility,
        processedAt: new Date().toISOString()
      }
    }
  };
}

/**
 * Generate preview data for user review
 * Filters out rows without Tag No. if Tag No. mapping is provided
 * @param {Array} headers - Excel headers
 * @param {Array} dataRows - Excel data rows
 * @param {Object} columnMappings - Column mappings
 * @param {number} limit - Number of rows to preview
 * @param {Object} options - Additional options including facility filtering
 * @returns {Array} Preview data
 */
export function generatePreviewData(headers, dataRows, columnMappings, limit = 10, options = {}) {
  const { facilityColumn, filterByFacility } = options;

  // Find the Tag No. column mapping
  const tagNoColumn = Object.keys(columnMappings).find(col => columnMappings[col] === 'name');
  const tagNoColumnIndex = tagNoColumn ? headers.indexOf(tagNoColumn) : -1;
  
  // Find the facility column index if facility filtering is enabled
  const facilityColumnIndex = facilityColumn ? headers.indexOf(facilityColumn) : -1;

  // Filter data rows to only include those with valid Tag No. and matching facility
  let filteredRows = dataRows;

  // First filter by Tag No.
  if (tagNoColumnIndex !== -1) {
    filteredRows = filteredRows.filter(row => {
      const tagNoValue = row[tagNoColumnIndex];
      return tagNoValue && String(tagNoValue).trim() !== '';
    });
  }
  
  // Then filter by facility if specified
  if (facilityColumnIndex !== -1 && filterByFacility) {
    filteredRows = filteredRows.filter(row => {
      const rowFacility = row[facilityColumnIndex];
      return rowFacility && String(rowFacility).trim().toLowerCase() === filterByFacility.toLowerCase();
    });
  }

  // Take the preview slice from filtered data
  const previewRows = filteredRows.slice(0, limit);
  
  return previewRows.map((row, index) => {
    const originalRowIndex = tagNoColumnIndex !== -1 ? 
      dataRows.indexOf(row) : // Find original position if filtered
      index; // Use current index if not filtered
      
    const previewRecord = {
      _rowNumber: originalRowIndex + 2, // +2 for header row and 1-based indexing
      _hasValidTagNo: true, // All preview records have valid Tag No.
    };
    
    // Include all original columns
    headers.forEach((header, colIndex) => {
      if (header && header.trim()) {
        previewRecord[header] = row[colIndex] || '';
      }
    });
    
    // Add mapped values as computed properties
    Object.entries(columnMappings).forEach(([excelColumn, avaField]) => {
      if (previewRecord[excelColumn]) {
        previewRecord[`_mapped_${avaField}`] = previewRecord[excelColumn];
      }
    });
    
    return previewRecord;
  });
}
